import { formatCurrency } from "@angular/common";
import { Component, ElementRef, Inject, Input, LOCALE_ID, OnChanges, OnInit, SimpleChanges, ViewChild } from "@angular/core";
import { ChartJsContainerComponent } from "@tylertech/chart-js";
import { NavigationService, UrlService } from "@tylertech/core-module";
import { Utilities } from "@tylertech/shared-module";
import { AccountDetailViewModel, AppLinkParms, ExecutionResult, FilterItem } from "@tylertech/tyler-api-types";
import { ActiveElement, Chart, ChartConfiguration, ChartEvent, ChartType, TooltipItem } from "chart.js";

@Component({
    selector: "account-detail-pie-chart",
    templateUrl: "./accountDetailPieChart.tmpl.html",
    styleUrls: [
        "./accountDetailPieChart.component.scss"
    ],
    standalone: false
})
export class AccountDetailPieChartComponent implements OnInit, OnChanges
{
    private colors: Array<string> = ["#008F8C", "#0D2644"];
    private labels: Array<string> = ["Budget Available", "Budget Used"];
    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;
    chartConfig: ChartConfiguration<"pie">;

    @Input() account: AccountDetailViewModel;
    @Input() appName: string;

    constructor(
        private readonly $navigationService: NavigationService,
        private readonly $urlService: UrlService,
        @Inject(LOCALE_ID) private readonly locale: string)
    { }

    get hasData(): boolean
    {
        const datasets = this.chartConfig?.data.datasets;
        if (!datasets)
        {
            return false;
        }
        const [budgetRemaining, budgetUsed] = datasets[0].data as [number, number];
        return !isNaN(budgetRemaining) && (budgetRemaining !== budgetUsed || budgetRemaining !== 0);
    }

    ngOnInit(): void
    {
        this.chartConfig = {
            type: "pie",
            data: {
                labels: this.labels,
                datasets: [
                    {
                        label: "",
                        data: this.getBudgetAmounts(),
                        backgroundColor: this.colors,
                    }
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: true,
                        position: "bottom",
                        onClick: null,
                        labels: {
                            color: "#333333"
                        }
                    },
                    tooltip: {
                        callbacks: {
                            label: (tooltipItem: TooltipItem<ChartType>): string | Array<string> =>
                            {
                                const label = this.labels[tooltipItem.dataIndex];
                                let retStr = label + ": ";
                                if (label === "Budget Used")
                                {
                                    const bu = this.account.Activity + (this.account.Encumbrance || 0) + (this.account.Reserves || 0);
                                    retStr += formatCurrency(bu, this.locale, "$");
                                }
                                else
                                {
                                    let ba = this.account.BudgetRemaining;
                                    if (!Utilities.IsNullOrUndefined(this.account.Pending))
                                    {
                                        ba = ba - this.account.Pending;
                                    }
                                    retStr += formatCurrency(ba, this.locale, "$");
                                }
                                return retStr;
                            },
                        },
                        backgroundColor: '#ffffff',
                        borderColor: '#767676',
                        borderWidth: 1
                    },
                },
                onClick: (event: ChartEvent & Event, _elements: ActiveElement[], _chart: Chart): void =>
                {
                    event?.stopPropagation();

                    if (this.appName)
                    {
                        this.$navigationService.navigateToApp(this.appName);
                    }
                    else
                    {
                        const filter: Array<FilterItem> = [
                            {
                                Key: "AccountType",
                                Value: this.account.AccountType
                            }
                        ];
                        const parms: AppLinkParms = {
                            App: "glaccountdet",
                            Parms: filter,
                        };
                        this.$urlService.GetAppLink(parms)
                            .then((executionResult: ExecutionResult<string>) =>
                            {
                                const index = executionResult.Result.indexOf("?");
                                if (executionResult.Result.includes("apps/form"))
                                {
                                    this.$navigationService.navigateToApp(executionResult.Result);
                                }
                            });
                    }
                },
            },
        };
    }

    ngOnChanges(changes: SimpleChanges): void
    {
        if (!changes.account || changes.account.currentValue === changes.account.previousValue)
        {
            return;
        }

        if (!this.chartConfig)
        {
            return;
        }

        this.chartConfig.data.datasets[0].data = this.getBudgetAmounts();
        this.chartContainer.update();
    }

    private getBudgetAmounts(): [number, number]
    {
        if (!this.account)
        {
            return [0, 0];
        }

        let budgetRemaining = this.account.BudgetRemaining;
        let budgetUsed = this.account.Activity + (this.account.Encumbrance || 0) + (this.account.Reserves || 0);
        if (this.account.Pending)
        {
            budgetRemaining = budgetRemaining - this.account.Pending;
        }

        if (this.account.CurrentBudget < 0)
        {
            // This is a Revenue account
            budgetRemaining = Math.abs(budgetRemaining);
            budgetUsed = Math.abs(budgetUsed);
        }
        else if (budgetRemaining < 0)
        {
            budgetRemaining = 0;
            budgetUsed = Math.abs(budgetUsed);
        }
        else
        {
            budgetUsed = Math.abs(budgetUsed);
        }

        return [budgetRemaining, budgetUsed];
    }
}
